import React from 'react';
import { Gift, AlertTriangle, Trash2, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { format } from 'date-fns';

const PHCreditsSection = ({ 
  phCredits, 
  phBalance, 
  selectedEmployee, 
  onDeletePHCredit, 
  onSetPHExpiry,
  isViewer 
}) => {
  if (!selectedEmployee || selectedEmployee === 'all') return null;
  
  return (
    <div className="mt-6 pt-6 border-t">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Gift className="h-4 w-4 text-purple-600" />
          Public Holiday Credits
        </h3>
        {phBalance && (
          <Badge variant="outline" className="bg-purple-50 text-purple-700">
            {phBalance.available_ph_days} days available
          </Badge>
        )}
      </div>
      
      {phBalance && phBalance.expiring_within_30_days > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-orange-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {phBalance.expiring_within_30_days} PH day(s) expiring within 30 days
          </p>
        </div>
      )}
      
      {!phCredits || phCredits.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No PH credits recorded</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {phCredits.map((credit) => {
            const today = new Date();
            const hasExpiry = !!credit.expiry_date;
            const expiryDate = hasExpiry ? new Date(credit.expiry_date) : null;
            const daysUntilExpiry = hasExpiry ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
            const isAuto = credit.source === 'auto_public_holiday';
            const expiringSoon = hasExpiry && daysUntilExpiry <= 30;
            
            return (
              <div 
                key={credit.id} 
                className={`p-3 rounded-lg border ${
                  credit.is_expired 
                    ? 'bg-red-50 border-red-200' 
                    : credit.is_used 
                    ? 'bg-gray-50 border-gray-200'
                    : expiringSoon
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-purple-50 border-purple-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      {credit.days} day{credit.days > 1 ? 's' : ''} earned
                      {isAuto && (
                        <Badge variant="outline" className="bg-violet-100 text-violet-700 text-[10px] py-0">Auto · Public Holiday</Badge>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      Earned: {format(new Date(credit.earned_date), 'MMM dd, yyyy')}
                    </p>
                    {credit.reason && (
                      <p className="text-xs text-gray-500 mt-1">{credit.reason}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {credit.is_expired ? (
                      <Badge variant="outline" className="bg-red-100 text-red-700">Expired</Badge>
                    ) : credit.is_used ? (
                      <Badge variant="outline" className="bg-gray-100 text-gray-700">Used</Badge>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Badge variant="outline" className={
                          !hasExpiry ? 'bg-green-100 text-green-700'
                          : expiringSoon ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }>
                          {hasExpiry ? `${daysUntilExpiry} days left` : 'No expiry'}
                        </Badge>
                        {!isViewer && onSetPHExpiry && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-violet-600"
                            title="Set / change expiry"
                            onClick={() => onSetPHExpiry(credit.id, credit.expiry_date || '')}
                            data-testid={`ph-set-expiry-${credit.id}`}
                          >
                            <CalendarClock className="h-3 w-3" />
                          </Button>
                        )}
                        {!isViewer && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 w-6 p-0 text-red-600"
                            onClick={() => onDeletePHCredit(credit.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {hasExpiry ? `Expires: ${format(new Date(credit.expiry_date), 'MMM dd, yyyy')}` : 'Never expires'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PHCreditsSection;
